// src/components/performance/InvoiceRequestModal.tsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Divider,
  CircularProgress
} from '@mui/material';
import { Receipt } from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';

interface InvoiceRequestModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (data: InvoiceRequestData) => Promise<void>;
  performanceData: {
    bandName: string;
    contactEmail: string;
    contactName: string;
    eventDate: string;
    code: string | null;
    venue: string;
    activityName?: string;
    fee: number;
    programmingId?: string; // Add this prop
  };
}

export interface InvoiceRequestData {
  recipient: string;
  cc: string[];
  subject: string;
  eventName: string;
  eventDate: string;
  eventLocation: string;
  activityName?: string;
  fee: number;
}

const InvoiceRequestModal: React.FC<InvoiceRequestModalProps> = ({
  open,
  onClose,
  onSend,
  performanceData
}) => {
  const [recipient, setRecipient] = useState(performanceData.contactEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [departmentCode, setDepartmentCode] = useState<string | null>(performanceData.code);

  // Fetch department code from programming if not already available
  useEffect(() => {
    const fetchDepartmentCode = async () => {
      if (performanceData.code || !performanceData.programmingId) return;
      
      try {
        const response = await axiosInstance.get(`/api/programmings/${performanceData.programmingId}`);
        if (response.data && response.data.department) {
          setDepartmentCode(response.data.department);
        }
      } catch (error) {
        console.error('Failed to fetch department code:', error);
      }
    };

    fetchDepartmentCode();
  }, [performanceData.programmingId, performanceData.code]);

  const handleSend = async () => {
    if (!recipient) {
      setError('Recipient email is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await onSend({
        recipient,
        cc: [
          'production@improvisedmusic.ie',
          'accounts@improvisedmusic.ie',
          'admin@improvisedmusic.ie',
          'ken@improvisedmusic.ie'
        ],
        subject: `Invoice Request: ${performanceData.bandName} (${departmentCode ?? 'n/a'}) - ${performanceData.eventDate}`,
        eventName: performanceData.bandName,
        eventDate: performanceData.eventDate,
        eventLocation: performanceData.venue,
        activityName: performanceData.activityName,
        fee: performanceData.fee
      });

      onClose();
    } catch (err) {
      console.error('Error sending invoice request:', err);
      setError('Failed to send invoice request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt color="primary" />
          <Typography variant="h6">Request Invoice</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ my: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Event Details
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography>
              <strong>Artist/Band:</strong> {performanceData.bandName}
            </Typography>
            <Typography>
              <strong>Contact Name:</strong>{' '}
              {performanceData.contactName || 'Not specified'}
            </Typography>
            <Typography>
              <strong>Event Date:</strong> {performanceData.eventDate}
            </Typography>
            <Typography>
              <strong>Venue:</strong> {performanceData.venue}
            </Typography>
            <Typography>
              <strong>Department Code:</strong>{' '}
              {departmentCode ?? 'n/a'}
            </Typography>
            {performanceData.activityName && (
              <Typography>
                <strong>Activity:</strong> {performanceData.activityName}
              </Typography>
            )}
            <Typography>
              <strong>Fee:</strong> €{performanceData.fee.toFixed(2)}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Email Details
          </Typography>

          <TextField
            fullWidth
            label="Recipient Email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            margin="normal"
            required
            error={!recipient}
            helperText={!recipient ? 'Recipient email is required' : ''}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The invoice request will include all necessary details including our
            address, required information for the invoice, and payment
            instructions.
          </Typography>

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={loading || !recipient}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? 'Sending...' : 'Send Invoice Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceRequestModal;